import "./shapes.scss";
import { useId } from "react";
import { Handle, Position } from "@xyflow/react";
import { NodeContent, NodeInputProps, VerticalFillDefinition } from "./common";
import type { HandlePosition } from "../layout";

/** The same union as everywhere else; `import type` so this adds no runtime dependency. */
type Side = HandlePosition;

/**
 * Per-side inline offsets, applied to BOTH the source and the target handle on that side.
 *
 * Keyed by side rather than by handle on purpose: the two handles on a side must sit at the same
 * point, or an inbound edge lands somewhere an outbound edge would not leave from.  Keeping them
 * as one entry makes that mismatch unrepresentable — it was previously a live bug on the
 * `multi-triangle` target-left handle, which nudged the wrong axis.
 *
 * The values come from `shapeGeometry.ts`, which is also what seats the routed endpoints, so the
 * handle a link attaches to and the point its route ends at are computed from one table.
 */
export type HandleOffsets = Partial<Record<Side, React.CSSProperties>>;

export type ShapeNodeConfig = {
    /** Class suffix: produces `FlowChart-node-${variant}`, which page CSS and the consumer's contract suites match on. */
    variant: string;
    viewBox: string;
    /** The shape body.  Receives the resolved paint, applied verbatim as the element's `style`. */
    shape: (paint: { fill: string | null; stroke: string | null }) => React.ReactNode;
    handleOffsets?: HandleOffsets;
};

const SIDES: { side: Side; position: Position }[] = [
    { side: "top", position: Position.Top },
    { side: "left", position: Position.Left },
    { side: "bottom", position: Position.Bottom },
    { side: "right", position: Position.Right },
];

/**
 * The eight handles every node exposes: a source and a target on each side.
 *
 * Shared with `Group.tsx`, which used to write all eight out by hand.  Both the ids and their DOM
 * order are a contract -- `getEdgeHandles` returns these ids for react-flow to resolve against,
 * and `nodeTypes.test.tsx` pins the order -- so having one copy is worth a component of its own.
 */
export function NodeHandles({ offsets = {} }: { offsets?: HandleOffsets }) {
    return <>
        {(["source", "target"] as const).map(type =>
            SIDES.map(({ side, position }) => (
                <Handle
                    key={`${type}-${side}`}
                    type={type}
                    id={`${type}-${side}`}
                    position={position}
                    style={offsets[side]}
                />
            ))
        )}
    </>;
}

/**
 * Builds one of the shape node components.  The eight handle ids are the layout contract
 * (`getEdgeHandles` selects by them), and the class names are pinned by the consumer's Python
 * contract suites, so both are reproduced exactly.
 */
export function createShapeNode({ variant, viewBox, shape, handleOffsets = {} }: ShapeNodeConfig) {
    function ShapeNode(props: NodeInputProps) {
        // React 19's `useId` returns a guillemet-wrapped id (`«r0»`), and this one does not just
        // label an element -- it goes into an SVG `url(#...)` fragment, where a non-ASCII
        // character is at the mercy of the browser's URL resolution.  Stripped rather than
        // hashed: what is left is still unique, because the uniqueness is in the counter.
        const id = useId().replace(/[^a-zA-Z0-9_-]/g, "");
        const verticalFillId = `vertical-fill-${id}`;
        const fillPercentage = props.data?.fillPercentage;
        const fill = fillPercentage !== undefined && fillPercentage !== null ? `url("#${verticalFillId}")` : null;

        return (
            <div className={`FlowChart-node FlowChart-node-${variant}`}>
                <NodeHandles offsets={handleOffsets} />

                <svg className="node-shape" viewBox={viewBox} preserveAspectRatio="none" width="100%">
                    <defs>
                        <VerticalFillDefinition fillPercentage={fillPercentage} id={verticalFillId} />
                    </defs>
                    {shape({ fill, stroke: fill })}
                </svg>

                <div className="node-content">
                    <NodeContent {...props} />
                </div>
            </div>
        );
    }

    ShapeNode.displayName = variant;
    return ShapeNode;
}
